import {
  Phone,
  MapPin,
  Clock,
  Mail,
  Shield,
  CheckCircle2,
  Star,
} from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { SEOHead } from "@/components/SEOHead";
import { GoogleMapsEmbed } from "@/components/GoogleMapsEmbed";

export default function Kontakt() {
  return (
    <>
      <SEOHead
        title="Kontakt | Miros Haushaltsauflösung"
        description="Kontaktieren Sie Miros Haushaltsauflösung für ein kostenloses Angebot in Berlin und Umgebung."
        canonical="https://miros-haushaltsaufloesung.de/kontakt"
      />
      <div className="flex flex-col w-full">
        {/* HERO */}
        <section className="relative w-full pt-32 pb-20 md:pt-40 md:pb-32 bg-[#0c2340] text-white overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-96 h-96 bg-accent/20 rounded-full blur-[100px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-96 h-96 bg-white/5 rounded-full blur-[100px] pointer-events-none"></div>

          <div className="container relative z-10 mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-left space-y-8">
                {/* Trust Badge */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm max-w-full">
                  <Star className="h-3 w-3 md:h-4 md:w-4 fill-yellow-400 text-yellow-400 shrink-0" />
                  <span className="text-xs md:text-sm font-semibold tracking-wide uppercase truncate md:whitespace-normal">
                    Top Bewertet in Berlin (5.0/5)
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black text-white leading-[1.1] tracking-tight">
                  Kontaktieren Sie uns
                </h1>
                <p className="text-lg md:text-xl text-white/80 font-medium max-w-xl leading-relaxed">
                  Wir sind für Sie da. Hinterlassen Sie uns eine Nachricht oder
                  rufen Sie uns direkt an für eine kostenlose und unverbindliche
                  Beratung.
                </p>

                {/* Trust Checkmarks */}
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-2">
                  <div className="flex items-center gap-2 text-white/90">
                    <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                    <span className="font-medium text-sm md:text-base">
                      Kostenlose Besichtigung
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
                    <span className="font-medium text-sm md:text-base">
                      Festpreisgarantie
                    </span>
                  </div>
                </div>
              </div>

              <div className="relative mx-auto w-full max-w-lg lg:max-w-none mt-8 lg:mt-0">
                {/* Image Glow */}
                <div className="absolute -inset-4 bg-accent/20 rounded-full blur-3xl -z-10"></div>

                <div className="relative h-[300px] sm:h-[400px] lg:h-[500px] rounded-sm overflow-hidden shadow-2xl border border-white/10">
                  <img
                    src="https://assets.cdn.filesafe.space/RcAk4NqqJiBX4hgBLewG/media/6a3a901a181eb301d4357a45.jpg"
                    alt="Kontakt"
                    width="800"
                    height="600"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </div>

                {/* Floating Trust Element 1 */}
                <div className="absolute -bottom-4 -left-2 md:-bottom-6 md:-left-8 bg-background p-3 md:p-4 rounded-sm shadow-xl border border-border flex items-center gap-3 md:gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 max-w-[90%] z-20">
                  <div className="bg-green-100 p-2 md:p-3 rounded-full shrink-0">
                    <Shield className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs md:text-sm font-bold text-foreground">
                      Zertifizierter Betrieb
                    </div>
                    <div className="text-[10px] md:text-xs text-muted-foreground">
                      Mit Haftpflichtversicherung
                    </div>
                  </div>
                </div>

                {/* Floating Trust Element 2 */}
                <div className="absolute -top-4 -right-2 md:-top-6 md:-right-6 bg-background p-3 md:p-4 rounded-sm shadow-xl border border-border flex items-center gap-2 md:gap-3 animate-in fade-in slide-in-from-top-4 duration-700 delay-500 z-20">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="h-3 w-3 md:h-4 md:w-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                  <div className="text-xs md:text-sm font-bold text-foreground">
                    5.0 auf Google
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MAIN CONTENT */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left: Info & Trust */}
              <div className="space-y-12">
                <div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6 text-foreground">
                    Wir freuen uns auf Ihre Anfrage
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Egal ob Entrümpelung, Haushaltsauflösung oder
                    Nachlassverwertung – wir beraten Sie gerne und erstellen
                    Ihnen ein Festpreisangebot.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg text-foreground">
                        Telefon & WhatsApp
                      </div>
                      <div className="flex flex-col gap-1">
                        <a
                          href="tel:015253181061"
                          className="text-muted-foreground hover:text-primary text-lg"
                        >
                          01525 3181061
                        </a>
                        <a
                          href="https://wa.me/4915253181061"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#15803d] hover:text-[#166534] text-base font-medium inline-flex items-center gap-1.5"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4 fill-current"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                          </svg>
                          Auf WhatsApp schreiben
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg text-foreground">
                        E-Mail
                      </div>
                      <div className="text-muted-foreground text-lg">
                        <a
                          href="mailto:info@miros-haushaltsaufloesung.de"
                          className="hover:text-primary"
                        >
                          info@miros-haushaltsaufloesung.de
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg text-foreground">
                        Adresse
                      </div>
                      <div className="text-muted-foreground text-lg">
                        Hauptstraße 43-44
                        <br />
                        13127 Berlin
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Inhaber: Miroslav Ozegovic
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-sm flex items-center justify-center shrink-0">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg text-foreground">
                        Öffnungszeiten
                      </div>
                      <div className="text-muted-foreground text-lg">
                        Mo - Sa: 8:00 - 20:00 Uhr
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Signals */}
                <div className="bg-secondary/30 p-6 rounded-sm border border-border mt-8">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2 text-foreground">
                    <Shield className="h-5 w-5 text-primary" /> Ihr Vertrauen
                    ist uns wichtig
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                      <span>Kostenlose & unverbindliche Besichtigung</span>
                    </li>
                    <li className="flex items-center gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                      <span>Verbindliche Festpreisgarantie</span>
                    </li>
                    <li className="flex items-center gap-3 text-muted-foreground">
                      <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                      <span>Betriebshaftpflichtversichert</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-background rounded-sm border border-border shadow-xl p-6 sm:p-8 md:p-10 h-fit sticky top-32">
                <h3 className="text-2xl font-bold mb-6 text-foreground">
                  Kostenloses Angebot anfordern
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </section>

        {/* MAP SECTION */}
        <section className="w-full bg-secondary/20">
          <GoogleMapsEmbed
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d617689.2544599194!2d12.484348240355132!3d52.789037222350245!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x23bf4a60473c084d%3A0x8a12d3b416c3d36a!2sMiros%20Haushaltsaufl%C3%B6sung!5e0!3m2!1sde!2sde!4v1771155121430!5m2!1sde!2sde"
            title="Miros Haushaltsauflösung Standort"
            height="500px"
          />
        </section>
      </div>
    </>
  );
}
